import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { AnyAction, Dispatch } from 'redux';
import { Container } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { addPlaceholderCard, getIndividualDetails, clearIndividualReducer } from './store/placeholder.actions';
import {
  getBrandDetails,
  getSeasonsDetails,
  getCategory1Details,
  getCategory2Details,
  getPCTypesDetails,
  getMultiSubBrandDetails,
  getMultipleCatgory,
  clearMultiSubBrandData,
  getCatBasedOnPT,
  getSubBrandAccess,
} from '../SystemMasters/Store/SystemMasters.action';
import { NewInput, NewSelectMultiSelect, toOptions } from '../../Components/PIM/common';
import CustomTooltip from '../../Components/Tooltip/CustomTooltip';
import { SaveIcon } from '../../bombiix-icon';
import PlaceholderDetailPage, { PlaceholderDetailPageHandle } from './PlaceholderDetailPage';
import FilterPlacement from './FilterPlacement';
import { usePlaceholderState } from './context/PlaceholderProvider';
import { useComponent } from '../UserSetting/UsersLibrary/UserRights';
import { months, YearsWithId , PLACEHOLDER_ROUTES } from './placeholder.constants';
import type { CardFormState, SelectOption, IdNameOption, PlaceholderIndividualDetail } from './types';
import './placeholder.css';
import { getCurrentYearLabel } from '../../Components/helper';

type RelatedRef = string | { _id?: string } | null | undefined;

interface MasterOption extends IdNameOption {
  isDefault?: boolean;
  productType?: RelatedRef | RelatedRef[];
  categoryOneId?: RelatedRef | RelatedRef[];
  catOneIds?: RelatedRef | RelatedRef[];
}

interface PlaceholderRootState {
  SystemMastersDetails: {
    brandList: MasterOption[];
    seasonList: MasterOption[];
    category1List: MasterOption[];
    category2List: MasterOption[];
    pcTypeList: MasterOption[];
    multiSubBrandList: MasterOption[];
    multipleCategory3: IdNameOption[];
    multipleCategory4: IdNameOption[];
  };
  UserDetails: {
    userInfo?: { userId?: string };
  };
  PlaceholderDetails: {
    individualDetail?: PlaceholderIndividualDetail | null;
  };
  Users: {
    logInRights?: unknown;
  };
}

type SelectValue = SelectOption | ReadonlyArray<SelectOption> | null;

type ThunkLike<R> = (dispatch: Dispatch<AnyAction>) => R;

interface AppDispatch {
  <R>(thunk: ThunkLike<R>): R;
  (action: AnyAction): AnyAction;
}

type CheckActionPermissionFn = (rights: unknown, moduleName: string, subModule: string | null, actionName: string) => boolean;

const INITIAL_FORM_STATE: CardFormState = {
  name: '',
  brand: [],
  subBrand: [],
  season: [],
  year: [],
  month: [],
  product_Type: [],
  category1: [],
  category2: [],
  category3: [],
  category4: [],
  category3List: [],
  category4List: [],
  status: [],
  hasError: false,
  isLoading: false,
  charCount: 0,
  subBrandAccess: false,
  isDefaultCard: false,
  fieldErrors: {
    brand: false,
    productType: false,
    category1: false,
    category2: false,
  },
};

/**
 * Build react-select `{ label, value }` options from an `{ _id, name }` master list.
 * Options whose value is in `lockedIds` are marked `isDisabled` so an already-saved
 * selection cannot be re-toggled off from the option list.
 */
const buildOptions = (list?: MasterOption[], lockedIds: Array<string | number> = []): SelectOption[] => {
  const lockedIdStrings = lockedIds.map(String);
  return (
    toOptions(list ?? [], {
      labelKey: 'name',
      valueKey: '_id',
      valueName: 'value',
      labelName: 'label',
    }) as unknown as SelectOption[]
  ).map((option) => ({ ...option, isDisabled: lockedIdStrings.includes(String(option.value)) }));
};

/**
 * Re-add any locked id that a change payload dropped, so a chip that was already
 * saved on the card cannot be removed via the multiselect's chip "x" control.
 */
const protectLockedIds = (selectedIds: string[], lockedIds: Array<string | number>): string[] => {
  const lockedIdStrings = lockedIds.map(String);
  const missingLocked = lockedIdStrings.filter((lockedId) => !selectedIds.includes(lockedId));
  return missingLocked.length ? [...selectedIds, ...missingLocked] : selectedIds;
};

/**
 * Resolve the currently selected ids into option objects sourced from a support
 * list, so saved (including inactive) values still render as chips.
 */
const resolveValue = (ids: Array<string | number>, supportList?: MasterOption[]): SelectOption[] => {
  const options = buildOptions(supportList);
  return options.filter((option) => (ids || []).some((id) => String(id) === String(option.value)));
};

/**
 * Extract the raw id list from a react-select change payload.
 */
const toSelectedIds = (selected: SelectValue): string[] => {
  if (!selected) return [];
  const arr = Array.isArray(selected) ? selected : [selected as SelectOption];
  return arr.map((option) => String(option.value));
};

const AddEditCardPage: React.FC = () => {
  const dispatch = useDispatch() as unknown as AppDispatch;
  const history = useHistory();
  const { id } = useParams<{ id?: string }>();
  const { t } = useTranslation('common');

  const {
    SystemMastersDetails: { brandList, seasonList, category1List, category2List, pcTypeList, multiSubBrandList, multipleCategory3, multipleCategory4 },
    UserDetails: { userInfo },
    PlaceholderDetails: { individualDetail },
    Users: { logInRights },
  } = useSelector((reducer: PlaceholderRootState) => reducer);
  const loggedInUserId = userInfo?.userId;
  const { totalCount } = usePlaceholderState();
  const [, setFilterMainState] = useState<{ selectedIdsArray: string[] }>({ selectedIdsArray: [] });

  const [formState, setFormState] = useState<CardFormState>(INITIAL_FORM_STATE);
  const placeholderDetailRef = useRef<PlaceholderDetailPageHandle>(null);
  const [catBasedOnPTData, setCatBasedOnPTData] = useState<boolean>(false);
  const [userRights, setUserRights] = useState<unknown>({});

  useEffect(() => {
    if (logInRights) {
      setUserRights(logInRights);
    }
  }, [logInRights]);

  const { CheckActionPermission } = useComponent();
  const checkAccess = CheckActionPermission as unknown as CheckActionPermissionFn;
  const PlaceholderAddEditAccess = checkAccess(userRights, 'placeholder', null, 'addEdit');

  useEffect(() => {
    const fetchFlag = async () => {
      try {
        const res = await dispatch(getCatBasedOnPT());
        setCatBasedOnPTData(!!res);
      } catch (e) {
        setCatBasedOnPTData(false);
      }
    };
    fetchFlag();
  }, [dispatch]);

  useEffect(() => {
    const fetchData = async () => {
      if (id) {
        await dispatch(getIndividualDetails(id));
      }
      const commonParam = {
        loggedInUserId,
        isActive: true,
      };
      await Promise.all([
        dispatch(getBrandDetails({ isActive: true })),
        dispatch(getSeasonsDetails(commonParam)),
        dispatch(getCategory1Details({ categoryType: 1, isActive: true })),
        dispatch(getCategory2Details({ categoryType: 2, isActive: true })),
        dispatch(getPCTypesDetails({ isActive: true, tier: 'Tier1' })),
      ]);
    };

    fetchData();
  }, [dispatch, id]);

  useEffect(() => {
    if (individualDetail) {
      setFormState((prev) => ({
        ...prev,
        name: individualDetail?.name || '',
        charCount: individualDetail?.name?.length || 0,
        brand: individualDetail?.brand?.map((b) => b._id) || [],
        subBrand: individualDetail?.subBrand?.map((sb) => sb._id) || [],
        season: individualDetail?.season?.map((s) => s._id) || [],
        year: individualDetail?.year || [],
        product_Type: individualDetail?.productType?.map((pt) => pt._id) || [],
        category1: individualDetail?.catOneId?.map((c1) => c1._id) || [],
        category2: individualDetail?.catTwoId?.map((c2) => c2._id) || [],
        month: individualDetail?.month || [],
        isDefaultCard: individualDetail?.isDefaultCard || false,
      }));
    }
  }, [individualDetail]);

  useEffect(() => {
    if (!isEmpty(formState.brand)) {
      dispatch(getMultiSubBrandDetails({ brandId: formState.brand }));
    }
  }, [formState.brand, dispatch]);

  useEffect(() => {
    if (!isEmpty(formState?.category1) && !isEmpty(formState?.category2)) {
      const callCategory3Api = async () => {
        const cat3params = {
          categoryOneId: formState?.category1,
          categoryTwoId: formState?.category2,
          categoryType: 3,
          isActive: true,
        };
        await dispatch(getMultipleCatgory(cat3params));
      };
      callCategory3Api();
    }
  }, [formState?.category1, formState?.category2, dispatch]);

  useEffect(() => {
    if (!isEmpty(formState?.category1) && !isEmpty(formState?.category2) && !isEmpty(formState?.category3)) {
      const callCategory4Api = async () => {
        const cat4params = {
          categoryOneId: formState?.category1,
          categoryTwoId: formState?.category2,
          categoryThreeId: formState?.category3,
          categoryType: 4,
          isActive: true,
        };
        await dispatch(getMultipleCatgory(cat4params));
      };
      callCategory4Api();
    }
  }, [formState?.category1, formState?.category2, formState?.category3, dispatch]);

  /**
   * Normalise a value or list into a flat array of ids, unwrapping `{ _id }` refs.
   */
  const toIds = (val: unknown): string[] => {
    if (!val) return [];
    const arr = Array.isArray(val) ? val : [val];
    return arr.map((v) => (typeof v === 'object' && v !== null ? (v as { _id?: string })._id : (v as string))).filter(Boolean) as string[];
  };

  /**
   * Pull the ids out of a master list of `{ _id }` records.
   */
  const extractIds = (list: unknown): string[] => (Array.isArray(list) ? (list.map((x) => (x as { _id?: string })?._id).filter(Boolean) as string[]) : []);

  /**
   * Keep only selected ids that are still present in the valid id set.
   */
  const pruneToValid = (sel: unknown, valid: string[]): string[] => (Array.isArray(sel) ? (sel as string[]).filter((selId) => valid.includes(selId)) : []);
  const lockedBrandIds = useMemo(() => (id ? individualDetail?.brand?.map((b) => b._id) ?? [] : []), [id, individualDetail]);
  const lockedSubBrandIds = useMemo(() => (id ? individualDetail?.subBrand?.map((sb) => sb._id) ?? [] : []), [id, individualDetail]);
  const lockedSeasonIds = useMemo(() => (id ? individualDetail?.season?.map((s) => s._id) ?? [] : []), [id, individualDetail]);
  const lockedMonthIds = useMemo(() => (id ? individualDetail?.month ?? [] : []), [id, individualDetail]);
  const lockedYearIds = useMemo(() => (id ? individualDetail?.year ?? [] : []), [id, individualDetail]);
  const lockedProductTypeIds = useMemo(() => (id ? individualDetail?.productType?.map((pt) => pt._id) ?? [] : []), [id, individualDetail]);
  const lockedCategory1Ids = useMemo(() => (id ? individualDetail?.catOneId?.map((c1) => c1._id) ?? [] : []), [id, individualDetail]);
  const lockedCategory2Ids = useMemo(() => (id ? individualDetail?.catTwoId?.map((c2) => c2._id) ?? [] : []), [id, individualDetail]);

  const selPT = toIds(formState?.product_Type);
  const selC1 = toIds(formState?.category1);

  const filteredC1 = useMemo<MasterOption[]>(() => {
    if (!catBasedOnPTData) return category1List;
    if (!selPT.length) return [];
    return (category1List || []).filter((c1) => {
      const rel = c1?.productType || [];
      const arr = Array.isArray(rel) ? rel : [rel];
      return arr.some((relId) => selPT.includes((relId as { _id?: string })?._id || (relId as string)));
    });
  }, [catBasedOnPTData, category1List, selPT]);

  const filteredC2 = useMemo<MasterOption[]>(() => {
    if (!catBasedOnPTData) return category2List;
    if (!selC1.length) return [];
    return (category2List || []).filter((c2) => {
      const rel = c2?.categoryOneId || c2?.catOneIds || [];
      const arr = Array.isArray(rel) ? rel : [rel];
      return arr.some((relId) => selC1.includes((relId as { _id?: string })?._id || (relId as string)));
    });
  }, [catBasedOnPTData, category2List, selC1]);

  const category1UnionOptions = useMemo<MasterOption[]>(() => {
    return catBasedOnPTData ? filteredC1 : category1List;
  }, [catBasedOnPTData, filteredC1, category1List]);

  const category2UnionOptions = useMemo<MasterOption[]>(() => {
    return catBasedOnPTData ? filteredC2 : category2List;
  }, [catBasedOnPTData, filteredC2, category2List]);

  useEffect(() => {
    if (formState?.category1 && !isEmpty(formState?.category1) && formState?.category2 && !isEmpty(formState?.category2)) {
      setFormState((oldData) => ({
        ...oldData,
        category3List: [...multipleCategory3],
      }));
    }
    if (
      formState?.category1 &&
      !isEmpty(formState?.category1) &&
      formState?.category2 &&
      !isEmpty(formState?.category2) &&
      formState?.category3 &&
      !isEmpty(formState?.category3)
    ) {
      setFormState((oldData) => ({
        ...oldData,
        category4List: [...multipleCategory4],
      }));
    }
  }, [multipleCategory3, multipleCategory4, formState?.category1, formState?.category2, formState?.category3]);

  useEffect(() => {
    const fetchLicensorAccess = async () => {
      const subBrandAccessRes = await dispatch(getSubBrandAccess());
      setFormState((prev) => ({ ...prev, subBrandAccess: subBrandAccessRes?.data || false }));
    };
    fetchLicensorAccess();
  }, [dispatch]);

  const onChange = (value: string | string[], name: string) => {
    switch (name) {
      case 'cpName': {
        const text = typeof value === 'string' ? value : '';
        setFormState((otherState) => ({
          ...otherState,
          name: /^\s/.test(text) ? '' : text,
          charCount: /^\s/.test(text) ? 0 : text.length,
          hasError: false,
        }));
        break;
      }
      case 'Brand': {
        const arr = protectLockedIds(Array.isArray(value) ? value : [], lockedBrandIds);
        setFormState((prevState) => ({
          ...prevState,
          brand: arr,
          fieldErrors: {
            ...prevState.fieldErrors,
            brand: !(arr.length > 0),
          },
        }));
        break;
      }
      case 'Sub Brand': {
        const arr = protectLockedIds(Array.isArray(value) ? value : [], lockedSubBrandIds);
        setFormState((prevState) => ({
          ...prevState,
          subBrand: arr,
        }));
        break;
      }
      case 'Months': {
        const arr = protectLockedIds(Array.isArray(value) ? value : [], lockedMonthIds);
        setFormState((prevState) => ({
          ...prevState,
          month: arr,
        }));
        break;
      }
      case 'Season': {
        const arr = protectLockedIds(Array.isArray(value) ? value : [], lockedSeasonIds);
        setFormState((prevState) => ({
          ...prevState,
          season: arr,
        }));
        break;
      }
      case 'Year': {
        const arr = protectLockedIds(Array.isArray(value) ? value : [], lockedYearIds);
        setFormState((prevState) => ({
          ...prevState,
          year: arr,
        }));
        break;
      }
      case 'Category 1': {
        const valueArr = protectLockedIds(Array.isArray(value) ? value : [], lockedCategory1Ids);
        setFormState((prevState) => ({
          ...prevState,
          category1: valueArr,
          category3List: [],
          category4List: [],
          fieldErrors: {
            ...prevState.fieldErrors,
            category1: !(valueArr.length > 0),
          },
        }));
        break;
      }
      case 'Category 2': {
        const valueArr = protectLockedIds(Array.isArray(value) ? value : [], lockedCategory2Ids);
        setFormState((prevState) => ({
          ...prevState,
          category2: valueArr,
          category3List: [],
          category4List: [],
          fieldErrors: {
            ...prevState.fieldErrors,
            category2: !(valueArr.length > 0),
          },
        }));
        break;
      }
      case 'Product Type': {
        const arr = protectLockedIds(Array.isArray(value) ? value : [], lockedProductTypeIds);
        setFormState((prevState) => ({
          ...prevState,
          product_Type: arr,
          fieldErrors: {
            ...prevState.fieldErrors,
            productType: !(arr.length > 0),
          },
        }));
        break;
      }
      default:
        break;
    }
  };

  const handleSelect = (val: string[], key: string) => {
    if (!catBasedOnPTData) {
      const nameMap: Record<string, string> = {
        product_Type: 'Product Type',
        category1: 'Category 1',
        category2: 'Category 2',
      };
      return onChange(val, nameMap[key] as string);
    }
    setFormState((prev) => {
      const next: CardFormState = { ...prev };
      const updateFieldError = (fieldKey: 'brand' | 'productType' | 'category1' | 'category2', arr: string[]) => {
        const hasVal = Array.isArray(arr) && arr.length > 0;
        next.fieldErrors = { ...next.fieldErrors, [fieldKey]: !hasVal };
      };

      if (key === 'product_Type') {
        next.product_Type = protectLockedIds([...(val || [])], lockedProductTypeIds);
        updateFieldError('productType', next.product_Type);
        const newPTIds = toIds(next.product_Type);
        if (!newPTIds.length) {
          next.category1 = [];
          updateFieldError('category1', next.category1);
          next.category2 = [];
          updateFieldError('category2', next.category2);
        } else {
          const validC1Options = (category1List || []).filter((c1) => {
            const rel = c1?.productType || [];
            const arr = Array.isArray(rel) ? rel : [rel];
            return arr.some((rid) => newPTIds.includes((rid as { _id?: string })?._id || (rid as string)));
          });
          const validC1Ids = extractIds(validC1Options);
          next.category1 = protectLockedIds(pruneToValid(toIds(next.category1), validC1Ids), lockedCategory1Ids);
          updateFieldError('category1', next.category1);

          const c1IdsAfterPT = toIds(next.category1);
          if (!c1IdsAfterPT.length) {
            next.category2 = [];
            updateFieldError('category2', next.category2);
          } else {
            const validC2Options = (category2List || []).filter((c2) => {
              const rel = c2?.categoryOneId || c2?.catOneIds || [];
              const arr = Array.isArray(rel) ? rel : [rel];
              return arr.some((rid) => c1IdsAfterPT.includes((rid as { _id?: string })?._id || (rid as string)));
            });
            const validC2Ids = extractIds(validC2Options);
            next.category2 = protectLockedIds(pruneToValid(toIds(next.category2), validC2Ids), lockedCategory2Ids);
            updateFieldError('category2', next.category2);
          }
        }

        next.category3List = [];
        next.category4List = [];
        next.category3 = [];
        next.category4 = [];
      }

      if (key === 'category1') {
        next.category1 = protectLockedIds([...(val || [])], lockedCategory1Ids);
        updateFieldError('category1', next.category1);
        const c1Ids = toIds(next.category1);
        if (!c1Ids.length) {
          next.category2 = [];
          updateFieldError('category2', next.category2);
        } else {
          const validC2Ids = extractIds(filteredC2);
          next.category2 = protectLockedIds(pruneToValid(toIds(next.category2), validC2Ids), lockedCategory2Ids);
          updateFieldError('category2', next.category2);
        }

        next.category3List = [];
        next.category4List = [];
        next.category3 = [];
        next.category4 = [];
      }

      if (key === 'category2') {
        next.category2 = protectLockedIds([...(val || [])], lockedCategory2Ids);
        updateFieldError('category2', next.category2);

        next.category3List = [];
        next.category4List = [];
        next.category3 = [];
        next.category4 = [];
      }

      return next;
    });
  };

  const checkingNonNullValues = <T,>(array?: T[]): T[] | undefined => {
    return array?.filter((ele) => ele);
  };

  const redirectToListingPage = async () => {
    setFormState((prev) => ({
      ...prev,
      name: '',
      brand: [],
      subBrand: [],
      season: [],
      year: [],
      month: [],
      product_Type: [],
      category1: [],
      category2: [],
      category3: [],
      category4: [],
      category3List: [],
      category4List: [],
      hasError: false,
      isLoading: false,
      charCount: 0,
      subBrandAccess: false,
      fieldErrors: {
        brand: false,
        productType: false,
        category1: false,
        category2: false,
      },
    }));
    await dispatch(clearIndividualReducer());
    await dispatch(clearMultiSubBrandData());
    history.push(PLACEHOLDER_ROUTES.BASE);
  };

  const redirectToEditPage = (editId: string) => {
    history.push(`${PLACEHOLDER_ROUTES.CARD_EDIT}/${editId}`);
  };

  /**
   * Saves the card-level fields and then any pending grid draft rows.
   *
   * On the default "Full Range" card the card-level fields are read-only and
   * are not validated server-side, so the card payload is skipped entirely and
   * only the pending grid rows are persisted — otherwise the required-field
   * check below would block a save the user is allowed to make.
   */
  const onSubmitMethod = async () => {
    if (id) {
      const isGridValid = await placeholderDetailRef.current?.validateBeforeSave();
      if (isGridValid === false) return;
    }

    if (id && formState?.isDefaultCard) {
      await placeholderDetailRef.current?.saveDraftIfPending();
      return;
    }

    const errors = {
      brand: !formState?.brand || formState.brand.length === 0,
      productType: !formState?.product_Type || formState.product_Type.length === 0,
      category1: !formState?.category1 || formState.category1.length === 0,
      category2: !formState?.category2 || formState.category2.length === 0,
    };

    const hasErrors = errors.brand || errors.productType || errors.category1 || errors.category2 || formState?.name?.trim().length === 0;

    if (hasErrors) {
      setFormState((otherState) => ({
        ...otherState,
        hasError: !(formState?.name?.trim().length > 0),
        fieldErrors: errors,
      }));
      return;
    }

    const params: Record<string, unknown> = {
      name: formState?.name?.trim(),
      season: checkingNonNullValues(formState?.season),
      year: formState?.year,
      productType: checkingNonNullValues(formState?.product_Type),
      brand: checkingNonNullValues(formState?.brand),
      subBrand: checkingNonNullValues(formState?.subBrand),
      month: formState?.month,
      catOneId: checkingNonNullValues(formState?.category1),
      catTwoId: checkingNonNullValues(formState?.category2),
      catThreeId: checkingNonNullValues(formState?.category3),
      catFourId: checkingNonNullValues(formState?.category4),
      suppliers: checkingNonNullValues(formState?.suppliers),
    };
    if (id) {
      params._id = id;
    }
    const res = (await dispatch(addPlaceholderCard(params, t))) as unknown as string | undefined;
    if (res) {
      dispatch(getIndividualDetails(id || res));
      if (id) {
        await placeholderDetailRef.current?.saveDraftIfPending();
      }
      redirectToEditPage(res);
    }
  };

  const isFieldDisabled = formState?.isDefaultCard || !PlaceholderAddEditAccess;
  const isSaveDisabled = !PlaceholderAddEditAccess;

  return (
    <>
      <div className="page-title-filter sticky ">
        <div className="title-container d-flex align-items-center">
          <h4 className="title">{t('PLACEHOLDER_MANAGEMENT.PLACEHOLDER_TITLE')}</h4>
          <div className="ml-2 d-flex">
            <NewInput
              type="text"
              name="ProductCriticalPathName"
              placeholder={t('PLACEHOLDER_MANAGEMENT.PLACEHOLDER_TITLE')}
              value={formState?.name}
              className="w-200 ml-1"
              maxLength={30}
              onChange={(e) => onChange(e?.target?.value, 'cpName')}
              disabled={isFieldDisabled}
              invalid={formState?.hasError && !formState?.charCount}
              endAdornment={<span className="bombiix-input__counter">{formState?.charCount}/30</span>}
            />
          </div>
        </div>
        <div className="search-filter action-new-row">
          {id && (
            <>
              {totalCount > 0 && (
                <span className="mr-16 font-14 mb-0 pm-total-count-text">
                  {totalCount} {t('PLACEHOLDER_MANAGEMENT.PLACE_HOLDER_MANAGEMENT.TOTAL_RECORDS')}
                </span>
              )}
              <FilterPlacement
                setMainState={setFilterMainState as unknown as React.ComponentProps<typeof FilterPlacement>['setMainState']}
                subBrandAccess={formState.subBrandAccess}
              />
            </>
          )}
          <button onClick={redirectToListingPage} className="bombiix-btn btn-light ml-8" type="button">
            {t('APPROVE_REJECTS.BACK')}
          </button>
        </div>
      </div>
      <Container className="mt-24" fluid>
        <div className="pm-form-row">
          <div className="pm-form-col">
            <NewSelectMultiSelect
              isMulti
              label={`${t('PRODUCT_CRITICAL_PATH.ADD_EDIT_PRODUCT_CRITICAL_PATH.BRAND')}`}
              placeholder={t('PRODUCT_CRITICAL_PATH.ADD_EDIT_PRODUCT_CRITICAL_PATH.BRAND')}
              options={buildOptions(brandList, lockedBrandIds)}
              value={resolveValue(formState.brand, brandList)}
              onChange={(selected) => onChange(toSelectedIds(selected as SelectValue), 'Brand')}
              isDisabled={isFieldDisabled}
              invalid={formState.fieldErrors.brand}
              required
            />
          </div>
          {formState.subBrandAccess && (
            <div className="pm-form-col">
              <NewSelectMultiSelect
                isMulti
                label="Sub Brand"
                placeholder="Sub Brand"
                options={buildOptions(
                  multiSubBrandList?.filter((subBrand) => subBrand?.isActive),
                  lockedSubBrandIds,
                )}
                value={resolveValue(
                  formState.subBrand,
                  multiSubBrandList?.filter((subBrand) => subBrand?.isActive),
                )}
                onChange={(selected) => onChange(toSelectedIds(selected as SelectValue), 'Sub Brand')}
                isDisabled={isFieldDisabled}
              />
            </div>
          )}
          <div className="pm-form-col">
            <NewSelectMultiSelect
              isMulti
              label={`${t('PRODUCT_CRITICAL_PATH.ADD_EDIT_PRODUCT_CRITICAL_PATH.PRODUCT_TYPE')}`}
              placeholder={t('PRODUCT_CRITICAL_PATH.ADD_EDIT_PRODUCT_CRITICAL_PATH.PRODUCT_TYPE')}
              options={buildOptions(pcTypeList, lockedProductTypeIds)}
              value={resolveValue(formState.product_Type, pcTypeList)}
              onChange={(selected) => handleSelect(toSelectedIds(selected as SelectValue), 'product_Type')}
              isDisabled={isFieldDisabled}
              invalid={formState.fieldErrors.productType}
              required
            />
          </div>
          <div className="pm-form-col">
            <NewSelectMultiSelect
              isMulti
              label={t('PRODUCT_CRITICAL_PATH.ADD_EDIT_PRODUCT_CRITICAL_PATH.SEASON')}
              placeholder={t('PRODUCT_CRITICAL_PATH.ADD_EDIT_PRODUCT_CRITICAL_PATH.SEASON')}
              options={buildOptions(seasonList, lockedSeasonIds)}
              value={resolveValue(formState.season, seasonList)}
              onChange={(selected) => onChange(toSelectedIds(selected as SelectValue), 'Season')}
              isDisabled={isFieldDisabled}
            />
          </div>
          <div className="pm-form-col">
            <NewSelectMultiSelect
              isMulti
              label={t('PRODUCT_CRITICAL_PATH.ADD_EDIT_PRODUCT_CRITICAL_PATH.YEAR')}
              placeholder={t('PRODUCT_CRITICAL_PATH.ADD_EDIT_PRODUCT_CRITICAL_PATH.YEAR')}
              options={buildOptions(YearsWithId(), lockedYearIds)}
              value={resolveValue(formState.year, YearsWithId())}
              onChange={(selected) => onChange(toSelectedIds(selected as SelectValue), 'Year')}
              isDisabled={isFieldDisabled}
              scrollToLabel={getCurrentYearLabel()}
            />
          </div>
          <div className="pm-form-col">
            <NewSelectMultiSelect
              isMulti
              label={t('PRODUCT_CRITICAL_PATH.ADD_EDIT_PRODUCT_CRITICAL_PATH.MONTHS')}
              placeholder={t('PRODUCT_CRITICAL_PATH.ADD_EDIT_PRODUCT_CRITICAL_PATH.MONTHS')}
              options={buildOptions(months, lockedMonthIds)}
              value={resolveValue(formState.month, months)}
              onChange={(selected) => onChange(toSelectedIds(selected as SelectValue), 'Months')}
              isDisabled={isFieldDisabled}
            />
          </div>
          <div className="pm-form-col">
            <NewSelectMultiSelect
              isMulti
              label={`${t('PRODUCT_CRITICAL_PATH.ADD_EDIT_PRODUCT_CRITICAL_PATH.CATEGORY_1')}`}
              placeholder={t('PRODUCT_CRITICAL_PATH.ADD_EDIT_PRODUCT_CRITICAL_PATH.CATEGORY_1')}
              options={buildOptions(
                category1UnionOptions.filter((cat1) => cat1?.isActive),
                lockedCategory1Ids,
              )}
              value={resolveValue(formState.category1, category1UnionOptions)}
              onChange={(selected) => handleSelect(toSelectedIds(selected as SelectValue), 'category1')}
              isDisabled={isFieldDisabled}
              invalid={formState.fieldErrors.category1}
              required
            />
          </div>
          <div className="pm-form-col">
            <NewSelectMultiSelect
              isMulti
              label={`${t('PRODUCT_CRITICAL_PATH.ADD_EDIT_PRODUCT_CRITICAL_PATH.CATEGORY_2')}`}
              placeholder={t('PRODUCT_CRITICAL_PATH.ADD_EDIT_PRODUCT_CRITICAL_PATH.CATEGORY_2')}
              options={buildOptions(
                category2UnionOptions.filter((cat2) => cat2?.isActive),
                lockedCategory2Ids,
              )}
              value={resolveValue(formState.category2, category2UnionOptions)}
              onChange={(selected) => handleSelect(toSelectedIds(selected as SelectValue), 'category2')}
              isDisabled={isFieldDisabled}
              invalid={formState.fieldErrors.category2}
              required
            />
          </div>
        </div>
        {!id && (
          <div className="floting-actions pm-floating-actions">
            <CustomTooltip placement="top" tooltipText={t('COMMON_LABEL.SAVE')}>
              <button
                type="button"
                className="btn d-inline-flex p-0"
                onClick={onSubmitMethod}
                aria-label={t('PRODUCT_CRITICAL_PATH.ADD_EDIT_PRODUCT_CRITICAL_PATH.SAVE_BUTTON')}
                disabled={isFieldDisabled}
              >
                <SaveIcon size={32} />
              </button>
            </CustomTooltip>
          </div>
        )}
      </Container>
      {id ? <PlaceholderDetailPage embedded ref={placeholderDetailRef} onSave={onSubmitMethod} saveDisabled={isSaveDisabled} /> : null}
    </>
  );
};

export default AddEditCardPage;
